import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import { usePricing } from "../contexts/PricingContext";
import useAuthToken from "../hooks/useAuthToken";
import { showToast } from "../components/ToastNotification";
import { formatDate } from "../utils/dateUtils";
import { FaRocket, FaChartLine, FaCreditCard, FaCog, FaHistory, FaShieldAlt } from "react-icons/fa";
import analytics from "../services/analytics";

const Dashboard = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { pricing } = usePricing();
  const { fetchWithAuth } = useAuthToken();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [userPlan, setUserPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/auth/signin");
      return;
    }
    
    // Check for payment success from Stripe redirect
    const paymentSuccess = searchParams.get('payment_success');
    const plan = searchParams.get('plan');
    const period = searchParams.get('period');
    const price = searchParams.get('price');
    
    if (paymentSuccess === 'true' && plan) {
      // Track successful subscription checkout
      analytics.trackSubscriptionCheckoutComplete(
        plan,
        period || 'unknown',
        price ? parseFloat(price) : 0
      );
      
      // Show success message
      showToast(`Successfully subscribed to ${plan} plan! Welcome to your dashboard.`, "success");
      
      // Clean URL by removing query parameters
      setSearchParams({});
    }
    
    fetchPlanStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const fetchPlanStatus = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_BASE_URL}/api/stripe/plan-status`);
      
      if (!response.ok) {
        throw new Error("Failed to fetch plan status");
      }
      
      const data = await response.json();
      setUserPlan(data);
      
      // Update localStorage for other components
      const storage = localStorage.getItem("access_token") ? localStorage : sessionStorage;
      storage.setItem("plan_status", JSON.stringify(data));
      
    } catch (error) {
      console.error("Error fetching plan status:", error);
      showToast("Failed to load plan information", "error");
    } finally {
      setLoading(false);
    }
  };

  // Icon and color mapping for each plan level
  const planUIConfig = {
    basic: { icon: FaRocket, color: "bg-blue-500" },
    premium: { icon: FaChartLine, color: "bg-purple-500" },
    enterprise: { icon: FaCreditCard, color: "bg-red-500" }
  };

  const getAvailableTools = () => {
    // Check if user has access via plan OR signup credits
    const hasAccess = userPlan?.plan || (userPlan?.signup_credits > 0);
    
    if (!hasAccess || !pricing || Object.keys(pricing).length === 0) return [];
    
    // If no plan but has signup credits, give basic access
    const userPlanName = userPlan.plan ? userPlan.plan.toLowerCase() : 'basic';
    
    // Determine the highest plan level the user has access to
    let userPlanLevel = "basic"; // default
    if (userPlanName.includes("enterprise")) userPlanLevel = "enterprise";
    else if (userPlanName.includes("premium")) userPlanLevel = "premium";
    // Basic plan
    else if (userPlanName.includes("basic")) userPlanLevel = "basic";
    
    // Get plan data from PricingContext
    const planData = pricing[userPlanLevel];
    
    // If user has signup credits but no plan, show basic tool
    if (!userPlan.plan && userPlan.signup_credits > 0) {
      return [{
        id: 'basic',
        title: "Risk Assessment",
        description: "Analyze business risks using AI-powered assessment",
        icon: FaShieldAlt,
        route: '/products/risk-assessment',
        available: true,
        planRequired: 'Free Trial',
        color: 'bg-blue-500'
      }];
    }
    
    if (!planData) {
      // If pricing data is not available, show a basic fallback
      return [{
        id: userPlanLevel,
        title: userPlanLevel.charAt(0).toUpperCase() + userPlanLevel.slice(1) + " Risk Assessment",
        description: "Risk assessment tool for your plan",
        icon: planUIConfig[userPlanLevel].icon,
        route: `/products/${userPlanLevel === 'basic' ? 'risk-assessment' : userPlanLevel === 'premium' ? 'premium-assessment' : 'enterprise-assessment'}`,
        available: true,
        planRequired: userPlanLevel.charAt(0).toUpperCase() + userPlanLevel.slice(1),
        color: planUIConfig[userPlanLevel].color
      }];
    }
    
    // Get UI config for this plan level
    const uiConfig = planUIConfig[userPlanLevel];
    
    // Return the tool with data from PricingContext + UI config
    return [{
      id: userPlanLevel,
      title: planData.title + " Risk Assessment",
      description: planData.description,
      icon: uiConfig.icon,
      route: planData.route,
      available: !planData.coming_soon,
      planRequired: planData.title,
      color: uiConfig.color
    }];
  };

  const formatPlanName = (plan) => {
    if (!plan) return "No Plan";
    
    const planMap = {
      'basic_month': 'Basic Monthly',
      'basic_year': 'Basic Yearly',
      'premium_month': 'Premium Monthly',
      'premium_year': 'Premium Yearly',
      'enterprise_month': 'Enterprise Monthly',
      'enterprise_year': 'Enterprise Yearly'
    };
    
    return planMap[plan] || plan.charAt(0).toUpperCase() + plan.slice(1);
  };


  if (loading) {
    return <div className="text-center py-10">Loading dashboard...</div>;
  }

  const availableTools = getAvailableTools();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Dashboard</h1>
          <p className="mt-2 text-gray-600">Access your risk assessment tools and manage your account</p>
        </div>

        {/* Plan Status Card */}
        {userPlan && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {userPlan.plan ? 'Current Plan' : 'Account Status'}
                </h2>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {userPlan.plan ? formatPlanName(userPlan.plan) : 'Free Trial'}
                </p>
                <div className="flex items-center mt-2 space-x-4">
                  {/* Show plan credits if on a plan, otherwise show signup credits */}
                  {userPlan.plan ? (
                    <>
                      <span className="text-sm text-gray-600">
                        Credits Remaining: <span className="font-semibold text-green-600">{userPlan.credits || 0}</span>
                      </span>
                      <span className="text-sm text-gray-600">
                        Next Credit Reset: <span className="font-semibold">{formatDate(userPlan.limit_date)}</span>
                      </span>
                      <span className="text-sm text-gray-600">
                        Plan Renews: <span className="font-semibold">{formatDate(userPlan.end_date)}</span>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-gray-600">
                        Free Credits Remaining: <span className="font-semibold text-green-600">{userPlan.signup_credits || 0}</span>
                      </span>
                      <span className="text-sm text-gray-600">
                        Status: <span className="font-semibold">No active subscription</span>
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <Link
                  to="/profile"
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <FaCog className="mr-2 h-4 w-4" />
                  Manage Plan
                </Link>
                <Link
                  to="/products"
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Upgrade Plan
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Tools Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Tools</h2>
          
          {availableTools.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {userPlan?.signup_credits > 0 ? 'Loading Tools...' : 'No Active Plan'}
              </h3>
              <p className="text-gray-600">
                {userPlan?.signup_credits > 0 
                  ? 'Your risk assessment tools are loading...' 
                  : 'Subscribe to a plan to access risk assessment tools'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availableTools.map((tool) => {
                const IconComponent = tool.icon;
                return (
                  <div key={tool.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                    <div className="p-6">
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${tool.color} text-white mb-4`}>
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{tool.title}</h3>
                      <p className="text-gray-600 mb-4">{tool.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          {tool.planRequired} Plan
                        </span>
                        <Link
                          to={tool.route}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                        >
                          Launch Tool
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/profile"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaCog className="h-8 w-8 text-gray-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Account Settings</h3>
                <p className="text-gray-600">Manage your profile and preferences</p>
              </div>
            </div>
          </Link>

          <Link
            to="/products"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaRocket className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">View All Plans</h3>
                <p className="text-gray-600">Explore and upgrade your subscription</p>
              </div>
            </div>
          </Link>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaHistory className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Usage Analytics</h3>
                <p className="text-gray-600">Coming soon - track your usage</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;